/**
 * Measured field. The graticule and the parallel labels share one percentage
 * scale so the ruling actually registers. Coast is an irregular shoreline, not
 * three sine ribbons. The traverse is the only information-bearing geometry.
 */

const VIEW_W = 100
const VIEW_H = 100

/** Parallels at fixed percentages of the field height — ruling ticks must hit these. */
const parallels = [
  { label: '26°00′N', y: 18 },
  { label: '25°50′N', y: 48 },
  { label: '25°40′N', y: 78 },
]

const meridians = [
  { label: '55°40′E', x: 18 },
  { label: '55°50′E', x: 42 },
  { label: '56°00′E', x: 66 },
  { label: '56°10′E', x: 90 },
]

const stations = [
  { code: 'ST-01', name: 'Discovery', x: 22, y: 22 },
  { code: 'ST-02', name: 'Build', x: 58, y: 48 },
  { code: 'ST-03', name: 'Handover', x: 34, y: 76 },
]

/** Irregular coast — abstract tint region, not a portrait of a real shoreline. */
const coast =
  'M78 0 C76 6 81 10 79 16 C76 24 84 28 82 36 C79 44 87 48 84 56 C81 64 88 70 85 78 C82 86 89 92 86 100'

export function ChartField() {
  return (
    <div className="relative h-full min-h-[20rem] overflow-hidden border border-rule bg-sheet lg:min-h-[26rem]">
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Traverse from Discovery through Build to Handover"
      >
        {/* Land plate */}
        <g className="plate plate-land">
          <path
            d={`${coast} L100 100 L100 0 Z`}
            fill="var(--color-land)"
          />
          {/* Contours — one hairline weight */}
          <path
            d="M84 0 C82 8 88 14 86 22 C83 32 90 38 87 48 C84 58 91 64 88 74 C85 84 92 90 89 100"
            fill="none"
            stroke="var(--color-ink)"
            strokeOpacity="0.28"
            strokeWidth="0.35"
          />
          <path
            d="M90 0 C88 10 94 18 92 28 C89 40 96 48 93 58 C90 70 97 78 94 88 C92 94 96 98 95 100"
            fill="none"
            stroke="var(--color-ink)"
            strokeOpacity="0.22"
            strokeWidth="0.35"
          />
          <path
            d={coast}
            fill="none"
            stroke="var(--color-ink)"
            strokeOpacity="0.55"
            strokeWidth="0.45"
          />
        </g>

        {/* Graticule — ruled to the same percentages as the labels */}
        <g aria-hidden="true">
          {parallels.map((line) => (
            <line
              key={line.label}
              x1="0"
              y1={line.y}
              x2={VIEW_W}
              y2={line.y}
              stroke="var(--color-rule-faint)"
              strokeWidth="0.25"
            />
          ))}
          {meridians.map((line) => (
            <line
              key={line.label}
              x1={line.x}
              y1="0"
              x2={line.x}
              y2={VIEW_H}
              stroke="var(--color-rule-faint)"
              strokeWidth="0.25"
            />
          ))}
          {/* Finer intervening ticks */}
          {[8, 28, 38, 58, 68, 88].map((y) => (
            <line
              key={`hy-${y}`}
              x1="0"
              y1={y}
              x2={VIEW_W}
              y2={y}
              stroke="var(--color-rule-faint)"
              strokeOpacity="0.55"
              strokeWidth="0.15"
            />
          ))}
          {[6, 30, 54, 78].map((x) => (
            <line
              key={`vx-${x}`}
              x1={x}
              y1="0"
              x2={x}
              y2={VIEW_H}
              stroke="var(--color-rule-faint)"
              strokeOpacity="0.55"
              strokeWidth="0.15"
            />
          ))}
        </g>

        {/* Traverse plate */}
        <g className="plate plate-line">
          <polyline
            points="22,22 58,48 34,76"
            fill="none"
            stroke="var(--color-ink)"
            strokeWidth="0.55"
          />
          <polyline
            points="22,22 58,48 34,76"
            fill="none"
            stroke="var(--color-overprint)"
            strokeWidth="0.4"
            strokeDasharray="1.2 2.4"
          />
          {stations.map((s) => (
            <g key={s.code}>
              <circle
                cx={s.x}
                cy={s.y}
                r="1.4"
                fill="var(--color-sheet)"
                stroke="var(--color-ink)"
                strokeWidth="0.45"
              />
              <circle cx={s.x} cy={s.y} r="0.45" fill="var(--color-overprint)" />
            </g>
          ))}
        </g>

        {/* Parallel labels — sit on the same y as the ruled lines */}
        {parallels.map((line) => (
          <text
            key={line.label}
            x="1.5"
            y={line.y - 1.2}
            fill="var(--color-ink-muted)"
            fontSize="2.4"
            fontFamily="var(--font-code)"
            letterSpacing="0.08"
          >
            {line.label}
          </text>
        ))}

        {/* Meridian labels along the bottom */}
        {meridians.map((line) => (
          <text
            key={line.label}
            x={line.x}
            y="97.5"
            textAnchor="middle"
            fill="var(--color-ink-muted)"
            fontSize="2.2"
            fontFamily="var(--font-code)"
            letterSpacing="0.06"
          >
            {line.label}
          </text>
        ))}

        {/* North mark */}
        <g transform="translate(92, 6)" fill="var(--color-ink-muted)">
          <path d="M0 10 V2.5" stroke="currentColor" strokeWidth="0.35" />
          <path d="M0 0 L1.4 2.8 H-1.4 Z" fill="currentColor" />
          <text
            x="0"
            y="14"
            textAnchor="middle"
            fontSize="2.4"
            fontFamily="var(--font-code)"
          >
            N
          </text>
        </g>

        {/* Field legend */}
        <g className="plate plate-over" transform="translate(3, 92)">
          <line
            x1="0"
            y1="0"
            x2="8"
            y2="0"
            stroke="var(--color-ink)"
            strokeWidth="0.4"
          />
          <line
            x1="0"
            y1="0"
            x2="8"
            y2="0"
            stroke="var(--color-overprint)"
            strokeWidth="0.35"
            strokeDasharray="1.2 2.4"
          />
          <text
            x="10"
            y="0.8"
            fill="var(--color-ink-muted)"
            fontSize="2.2"
            fontFamily="var(--font-code)"
            letterSpacing="0.06"
          >
            Traverse — discovery to handover
          </text>
        </g>
      </svg>

      {/* Station nameplates — HTML so lettering stays sharp at any scale */}
      {stations.map((station) => (
        <div
          key={station.code}
          className="plate plate-over pointer-events-none absolute"
          style={{
            left: `${station.x}%`,
            top: `${station.y}%`,
            transform: 'translate(10px, -50%)',
          }}
        >
          <span className="datum block text-overprint">{station.code}</span>
          <span
            className="block text-sm font-semibold uppercase tracking-[0.08em] text-ink"
            style={{ fontStretch: '84%' }}
          >
            {station.name}
          </span>
        </div>
      ))}
    </div>
  )
}
